/** The offline capture queue.
 *
 *  Scope is deliberate: **captures only**, not every mutation. A create carries
 *  an `Idempotency-Key` and no `If-Match`, so replaying it is unambiguous and
 *  needs no conflict resolution. Transitions and edits are revision-guarded, so
 *  queueing them means holding a revision that may be stale by replay time —
 *  the design assumes that wider queue, and it is deferred, not rejected.
 *
 *  Two invariants matter more than they look:
 *
 *  1. The idempotency key is minted **at enqueue** and persisted with the
 *     operation. Generating it at send time would make every retry a new logical
 *     mutation, so one flaky connection becomes three identical entries.
 *  2. The journal date is pinned at enqueue too, by `Capture`. Without it the
 *     server resolves "today" when the replay lands, and a capture written on
 *     Sunday night appears on Monday.
 */

import { request, ApiError, type Session, describeError } from '../api/client';
import { newIdempotencyKey } from '../api/ids';
import { QUEUE_STORE, idb } from './db';
import type { Destination } from './destinations';

export interface QueuedCapture {
  id: string;
  destinationId: string;
  /** Minted once, here. Never regenerated on retry. */
  idempotencyKey: string;
  body: { text: string; date?: string | null; month?: string | null };
  /** For the UI: what the entry will say once it lands. */
  summary: string;
  createdAt: string;
  /** Replay attempts that failed for a reason worth showing. */
  attempts: number;
  lastError?: string;
}

export async function enqueue(
  destination: Destination,
  body: QueuedCapture['body'],
  summary: string,
  /** The key the capture was already sent under, when it was. A send that
   *  timed out may still have landed, and replaying it under a fresh key would
   *  file it twice; under the same key the server answers with the first one. */
  idempotencyKey: string = newIdempotencyKey(),
): Promise<QueuedCapture> {
  const item: QueuedCapture = {
    id: newIdempotencyKey(),
    destinationId: destination.id,
    idempotencyKey,
    body,
    summary,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  await idb.put(QUEUE_STORE, item);
  return item;
}

export async function pending(destination: Destination): Promise<QueuedCapture[]> {
  const all = await idb.all<QueuedCapture>(QUEUE_STORE);
  return all
    .filter((item) => item.destinationId === destination.id)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export interface ReplayReport {
  sent: number;
  failed: number;
  /** Set when the queue stopped early because the network went away again. */
  offline: boolean;
}

/** Send everything queued, oldest first.
 *
 *  Order is preserved and a failure stops the run rather than skipping ahead, so
 *  entries land in the order they were written. A 4xx that is not
 *  authentication is terminal — the request will never succeed — so it is
 *  dropped with its reason recorded rather than blocking every capture behind
 *  it forever.
 */
export async function replay(
  session: Session,
  destination: Destination,
): Promise<ReplayReport> {
  const report: ReplayReport = { sent: 0, failed: 0, offline: false };
  for (const item of await pending(destination)) {
    try {
      await request(session, '/v1/capture', {
        method: 'POST',
        idempotencyKey: item.idempotencyKey,
        body: item.body,
      });
      await idb.del(QUEUE_STORE, item.id);
      report.sent += 1;
    } catch (error) {
      if (error instanceof ApiError) {
        // 401/403 mean the credential is wrong, not the capture. Keep it and
        // stop: re-authenticating will send it.
        if (error.status === 401 || error.status === 403) {
          report.failed += 1;
          return report;
        }
        // The server already has this one; the receipt proves it landed.
        if (error.status === 409 && error.problem?.code === 'idempotency_key_reused') {
          await idb.del(QUEUE_STORE, item.id);
          report.sent += 1;
          continue;
        }
        if (error.status >= 400 && error.status < 500) {
          await idb.put(QUEUE_STORE, {
            ...item,
            attempts: item.attempts + 1,
            lastError: describeError(error),
          });
          report.failed += 1;
          continue;
        }
      }
      // Network error or 5xx: the connection went away. Stop and keep order.
      report.offline = true;
      return report;
    }
  }
  return report;
}

/** Queued items that will never send, so the UI can offer to discard them
 *  rather than showing a counter that never reaches zero. */
export async function stuck(destination: Destination): Promise<QueuedCapture[]> {
  return (await pending(destination)).filter((item) => item.attempts > 0 && item.lastError);
}

export async function discard(id: string): Promise<void> {
  await idb.del(QUEUE_STORE, id);
}
